import { prepareCgmReadingsForLogsApi } from "src/helpers/cgm";
import type { PocGlucoseJson } from "src/types/cgm.types";
import { bindQueryParams } from "src/helpers/tools";
import { methods, request } from "./base";

const CgmApis = {
  /**
   * Save batch of CGM glucose readings.
   * Matches MyTatva-RN: Device.postBatchCGMData → POST /cgm/logs
   * Payload: { logs: GlucoseData[], vendor: "GoodFlip" }
   */
  postBatchCGMData: async (payload: {
    logs: any[];
    vendor?: string;
    isHardResync?: boolean;
  }) =>
    await request({
      method: methods.POST,
      route: "/cgm/logs",
      priv: true,
      payload: { ...payload, vendor: payload.vendor ?? "GoodFlip" },
    }),

  /**
   * Same as postBatchCGMData but normalizes device rows: sort/dedupe by timeInMillis and set readingTimestamp (ISO).
   * Prefer this when uploading raw SDK readings so the backend is not forced to use y/m/d/h/minute alone.
   */
  postBatchCGMDataFromReadings: async (options: {
    readings: PocGlucoseJson[];
    vendor?: string;
    isHardResync?: boolean;
  }) => {
    const logs = prepareCgmReadingsForLogsApi(options.readings);
    return await request({
      method: methods.POST,
      route: "/cgm/logs",
      priv: true,
      payload: {
        logs,
        vendor: options.vendor ?? "GoodFlip",
        ...(options.isHardResync != null
          ? { isHardResync: options.isHardResync }
          : {}),
      },
    });
  },

  /**
   * GET /cgm/calendar — calendar overview of CGM data availability
   * Query: { month, year }
   */
  getCGMCalendar: async (queryParams: { month: number; year: number }) => {
    const route = bindQueryParams("/cgm/calendar", queryParams);
    return await request({ method: methods.GET, route, priv: true });
  },

  /**
   * GET /cgm/daily_logs — glucose readings for a specific calendar day (server-defined bucket).
   * If the API supports it, pass timeZone (e.g. Asia/Kolkata via REACT_APP_CGM_DAILY_LOGS_TIMEZONE)
   * so “today” matches the user’s local day. Response only includes rows the backend has stored;
   * gaps before the first successful POST/sync for that day are not a client bug.
   */
  getCGMDailyLogs: async (queryParams: {
    startDate: string;
    timeZone?: string;
  }) => {
    const q: Record<string, string> = { startDate: queryParams.startDate };
    if (queryParams.timeZone) q.timeZone = queryParams.timeZone;
    const route = bindQueryParams("/cgm/daily_logs", q);
    return await request({ method: methods.GET, route, priv: true });
  },

  /**
   * GET /cgm/cumulative_logs — aggregated stats over a date range
   */
  getCGMCumulativeLogs: async (queryParams: Record<string, string>) => {
    const route = bindQueryParams("/cgm/cumulative_logs", queryParams);
    return await request({ method: methods.GET, route, priv: true });
  },

  /**
   * GET /cgm/status — current CGM device/sensor status and remaining sensor life
   */
  getCGMStatus: async () =>
    await request({ method: methods.GET, route: "/cgm/status", priv: true }),

  /**
   * GET /cgm/health_logs — health logs for a given date
   * Query: { date: "YYYY-MM-DD" }
   */
  getCGMHealthLogs: async (queryParams: { date: string }) => {
    const route = bindQueryParams("/cgm/health_logs", queryParams);
    return await request({ method: methods.GET, route, priv: true });
  },

  /**
   * POST /cgm/expire-sensor — mark sensor as expired
   */
  expireSensor: async (payload: object) =>
    await request({
      method: methods.POST,
      route: "/cgm/expire-sensor",
      priv: true,
      payload,
    }),

  /**
   * POST /cgm/device_name — set device vendor name
   */
  setDeviceName: async (payload: { deviceName: "goodflip" | "diasens" }) =>
    await request({
      method: methods.POST,
      route: "/cgm/device_name",
      priv: true,
      payload,
    }),

  /**
   * GET /cgm/get_device_name — get currently set device vendor
   */
  getDeviceName: async () =>
    await request({
      method: methods.GET,
      route: "/cgm/get_device_name",
      priv: true,
    }),

  /**
   * GET /cgm/fetch_device_list — list of CGM devices
   */
  fetchDeviceList: async () =>
    await request({
      method: methods.GET,
      route: "/cgm/fetch_device_list",
      priv: true,
    }),

  /**
   * POST /cgm/upload_device_logs — upload CSV log file from device
   */
  uploadCgmLogFiles: async (formData: FormData) =>
    await request({
      method: methods.POST,
      route: "/cgm/upload_device_logs",
      priv: true,
      payload: formData,
    }),
};

export default CgmApis;
